import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Bell, Car, Check, ChevronDown, ChevronLeft, ChevronRight, TrendingUp, History, Scale } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "@/hooks/useSession";
import { supabase } from "@/lib/supabase";
import { fmtBR, fmtCarTitle } from "@/lib/seo-format";
import { AppNav } from "@/components/AppNav";
import { ModelCardImage } from "@/components/ModelCardImage";
import { ModelSearchBar } from "@/components/search/ModelSearchBar";
import { LANDING_FAQ } from "@/lib/landing-faq";
import { MODELOS } from "@/lib/seo-config";
import { fetchCatalogFacets, fetchPriceDropListings } from "@/lib/listings-search";
import type { SearchListing } from "@/lib/listings-search";
import { STAT_LISTINGS, STAT_SOURCES } from "@/lib/site-stats";
import { brl, cohortSearchHref, teaser } from "@/lib/price-reference";
import { ListingDetailDrawer, type Listing } from "@/components/ListingDetailDrawer";
import { LoginGateModal } from "@/components/LoginGateModal";
import { posthog } from "@/lib/posthog";

const AlertForm = lazy(() =>
  import("@/components/AlertModal").then((m) => ({ default: m.AlertForm }))
);

export const Route = createFileRoute("/")({
  component: Index,
});

function useCountUp(target: number | undefined, duration = 900): number {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (target == null) return;
    const start = Math.floor(target * 0.5);
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(Math.floor(start + (target - start) * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);
  return value;
}

function Index() {
  const { session } = useSession();
  const navigate = useNavigate();
  const [hasAlert, setHasAlert] = useState(false);
  const { data: facets } = useQuery({
    queryKey: ["home-facets"],
    queryFn: fetchCatalogFacets,
    staleTime: 10 * 60 * 1000,
  });
  const animatedTotal = useCountUp(facets?.total);
  const [statTimedOut, setStatTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStatTimedOut(true), 5000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!session) return;
    supabase
      .from("filters")
      .select("id", { count: "exact", head: true })
      .eq("is_active", true)
      .is("deleted_at", null)
      .then(({ count }) => setHasAlert((count ?? 0) > 0));
  }, [session]);

  return (
    <main className="min-h-screen text-foreground">
      {/* ─── Hero ──────────────────────────────────────────────────────── */}
      <div className="relative">
        {/* Background decorations clipped independently so the search dropdown can overflow */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="hero-grid absolute inset-0" />
          <div className="hero-glow" />
        </div>
        <AppNav maxWidth="max-w-6xl" />
        <div className="relative mx-auto max-w-3xl px-6 pb-12 pt-2 text-center sm:pt-6">
          <div className="chip mb-8 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium text-muted-foreground">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            {!facets && statTimedOut ? STAT_LISTINGS : animatedTotal.toLocaleString("pt-BR")} blindados ativos · atualizado todo dia
          </div>
          <h1 className="font-display text-5xl leading-[0.95] sm:text-6xl md:text-7xl">
            <span className="text-iridescent">O maior radar</span>
            <br />
            <span className="text-foreground/90">de blindados do Brasil.</span>
          </h1>
          <p className="mx-auto mt-7 max-w-xl text-base text-muted-foreground sm:text-lg">
            {!facets && statTimedOut ? STAT_LISTINGS : animatedTotal.toLocaleString("pt-BR")} blindados de {STAT_SOURCES} sites num lugar só. Busque, compare e clique direto no anunciante — sem cadastro.
          </p>
          <div className="mt-10 mx-auto w-full max-w-xl">
            <ModelSearchBar
              large
              onPick={(patch) => { const slug = patch.family ?? patch.model; if (slug) navigate({ to: "/buscar", search: { models: slug } }); }}
              onClear={() => {}}
            />
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/buscar" className="text-xs text-muted-foreground hover:text-foreground">
              Ver todos os anúncios →
            </Link>
            {session ? (
              <Link to="/alertas" className="text-xs text-muted-foreground hover:text-foreground">
                Meus alertas →
              </Link>
            ) : (
              <a href="#criar-alerta" className="text-xs text-muted-foreground hover:text-foreground">
                Criar alerta grátis →
              </a>
            )}
          </div>
        </div>
      </div>

      {/* ─── Modelos em destaque ───────────────────────────────────────── */}
      <FeaturedModels />

      {/* ─── Por faixa de preço ────────────────────────────────────────── */}
      <PriceBuckets />

      {/* ─── Preços de mercado ─────────────────────────────────────────── */}
      <MarketPrices />

      {/* ─── Baixaram de preço recentemente ───────────────────────────────────────────── */}
      <PriceDropCarousel />

      {/* ─── Features: header + mobile accordion ──────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 pt-12 pb-4 sm:pt-16 sm:pb-6">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl text-foreground sm:text-4xl">
            Compare, analise e decida.
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Tudo incluso na conta gratuita — sem cartão de crédito.
          </p>
        </div>

        {/* Mobile: accordion cards */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 md:hidden">
          <ProFeatureCard
            icon={<Bell className="h-4 w-4" />}
            title="Alertas"
            body="Você define o filtro, a gente monitora. Apareceu um anúncio que bate com a sua busca? Email na hora."
            detail={
              <div className="space-y-4">
                <AlertMockup />
                <ul className="space-y-1.5 text-sm text-foreground/75">
                  <Bullet>Filtre por modelo, ano, KM e preço</Bullet>
                  <Bullet>Notificação em minutos após publicação</Bullet>
                  <Bullet>Disponível na conta gratuita</Bullet>
                </ul>
              </div>
            }
          />
          <ProFeatureCard
            icon={<Scale className="h-4 w-4" />}
            title="Comparador"
            body="Coloque vários anúncios lado a lado. Preço, KM, ano e posição vs. média do modelo — sem planilha."
            detail={
              <div className="space-y-4">
                <ComparadorMockup />
                <ul className="space-y-1.5 text-sm text-foreground/75">
                  <Bullet>Múltiplos carros lado a lado</Bullet>
                  <Bullet>Posição vs. média do modelo</Bullet>
                  <Bullet>Disponível na conta gratuita</Bullet>
                </ul>
              </div>
            }
          />
          <ProFeatureCard
            icon={<History className="h-4 w-4" />}
            title="Histórico de preço"
            body="Esse carro já baixou de preço três vezes. Você sabe disso antes de ligar."
            detail={
              <div className="space-y-4">
                <HistoricoMockup />
                <ul className="space-y-1.5 text-sm text-foreground/75">
                  <Bullet>Linha do tempo completa</Bullet>
                  <Bullet>Quantas vezes o preço mudou</Bullet>
                  <Bullet>Disponível na conta gratuita</Bullet>
                </ul>
              </div>
            }
          />
          <ProFeatureCard
            icon={<TrendingUp className="h-4 w-4" />}
            title="Contexto de mercado"
            body="Quantos Discovery Sport estão à venda? Qual o preço médio? Veja na hora se é uma oportunidade."
            detail={
              <div className="space-y-4">
                <MercadoMockup />
                <ul className="space-y-1.5 text-sm text-foreground/75">
                  <Bullet>Estatísticas atualizadas todo dia</Bullet>
                  <Bullet>Posição do anúncio vs. mercado</Bullet>
                  <Bullet>Disponível na conta gratuita</Bullet>
                </ul>
              </div>
            }
          />
        </div>

        <div className="mt-8 md:hidden flex flex-col items-center gap-3">
          <Link
            to="/login"
            className="rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition hover:opacity-90"
          >
            Criar conta grátis
          </Link>
          <Link
            to="/planos"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Quer alertas automáticos? R$29/mês →
          </Link>
        </div>
      </section>

      {/* ─── Desktop: Alerta inteligente ───────────────────────────────── */}
      <section className="hidden md:block mx-auto max-w-6xl px-6 pt-6 pb-12 sm:pt-8 sm:pb-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.25em] text-primary">
              Alerta inteligente
            </p>
            <h2 className="mt-3 font-display text-3xl text-foreground sm:text-4xl">
              Defina uma vez. <em className="not-italic text-iridescent">Esqueça.</em>
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Você diz quais modelos, ano, KM, faixa de preço e localização te interessam.
              A gente vasculha 60+ sites todo dia e manda um email quando aparece algo novo que bate.
              Sem novidade no dia? Sem email. Zero ruído.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-foreground/80">
              <Bullet>Filtros por modelo, ano, KM, preço e estado</Bullet>
              <Bullet>Email só quando tem novidade — nada de spam</Bullet>
              <Bullet>Funciona até no plano gratuito</Bullet>
            </ul>
          </div>
          <FeatureMockup>
            <EmailPreview />
          </FeatureMockup>
        </div>
      </section>

      {/* ─── Desktop: Comparador ───────────────────────────────────────── */}
      <section className="hidden md:block mx-auto max-w-6xl px-6 py-12 sm:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <FeatureMockup>
            <div className="flex items-center gap-2 text-foreground/80">
              <Scale className="h-4 w-4 text-primary" />
              <span className="font-display text-sm">Comparador</span>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-xs tabular-nums">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    <th className="pb-2 pr-3 font-medium" />
                    <th className="pb-2 px-2 font-medium">Carro A</th>
                    <th className="pb-2 px-2 font-medium">Carro B</th>
                    <th className="pb-2 px-2 font-medium">Carro C</th>
                  </tr>
                </thead>
                <tbody className="text-foreground/80">
                  <CompareRow label="Ano"   a="2022"      b="2021"      c="2023" />
                  <CompareRow label="KM"    a="32mil"     b="58mil"     c="12mil" />
                  <CompareRow label="Preço" a="R$ 195k"   b="R$ 168k"   c="R$ 225k" />
                  <CompareRow
                    label="vs. média"
                    a={<span className="text-foreground/70">−2%</span>}
                    b={<span className="text-primary font-medium">−15% 🟢</span>}
                    c={<span className="text-foreground/70">+14%</span>}
                  />
                </tbody>
              </table>
            </div>
          </FeatureMockup>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.25em] text-primary">
              Comparador avançado
            </p>
            <h2 className="mt-3 font-display text-3xl text-foreground sm:text-4xl">
              Coloque os finalistas lado a lado e <em className="not-italic text-iridescent">decida sem achismo</em>.
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Preço, KM, ano e posição vs. média do modelo — tudo na mesma tela.
              Sem planilha, sem múltiplas abas, sem se perder.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-foreground/80">
              <Bullet>Múltiplos carros lado a lado</Bullet>
              <Bullet>Indicador de oportunidade vs. mercado</Bullet>
              <Bullet>Disponível na conta gratuita</Bullet>
            </ul>
          </div>
        </div>
      </section>

      {/* ─── Desktop: Histórico de preço ───────────────────────────────── */}
      <section className="hidden md:block mx-auto max-w-6xl px-6 py-12 sm:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.25em] text-primary">
              Histórico de preço
            </p>
            <h2 className="mt-3 font-display text-3xl text-foreground sm:text-4xl">
              O vendedor já baixou três vezes. <em className="not-italic text-iridescent">Você nem sabia.</em>
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Esse carro está há 60 dias no ar? Já baixou 20% em duas semanas?
              Acompanhamos a trajetória de cada anúncio desde o dia 1.
              Quando você liga, já sabe a história inteira.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-foreground/80">
              <Bullet>Linha do tempo completa do preço</Bullet>
              <Bullet>Quantas vezes o preço mudou</Bullet>
              <Bullet>Disponível na conta gratuita</Bullet>
            </ul>
          </div>
          <FeatureMockup>
            <div className="flex items-center gap-2 text-foreground/80">
              <History className="h-4 w-4 text-primary" />
              <span className="font-display text-sm">Histórico de preço</span>
            </div>
            <div className="mt-4">
              <p className="font-display text-base text-foreground">Discovery Sport HSE 2022</p>
              <p className="text-xs text-muted-foreground">São Paulo · 48.000 km</p>
            </div>
            <PriceSparkline />
            <div className="mt-3 flex items-end justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Inicial · há 38 dias</p>
                <p className="text-sm text-foreground/70 line-through">R$ 295.000</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.18em] text-primary">Hoje</p>
                <p className="text-lg font-medium text-foreground">R$ 268.000</p>
                <p className="text-[11px] text-primary">−R$ 27k em 38 dias</p>
              </div>
            </div>
          </FeatureMockup>
        </div>
      </section>

      {/* ─── Desktop: Inteligência de mercado ──────────────────────────── */}
      <section className="hidden md:block mx-auto max-w-6xl px-6 py-12 sm:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <FeatureMockup>
            <div className="flex items-center gap-2 text-foreground/80">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="font-display text-sm">Inteligência de Mercado</span>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <MarketCard
                model="Land Rover Discovery Sport"
                active="14"
                kmAvg="78mil"
                min="R$ 245k"
                avg="R$ 290k"
                max="R$ 340k"
              />
              <MarketCard
                model="Jeep Compass"
                active="32"
                kmAvg="42mil"
                min="R$ 165k"
                avg="R$ 198k"
                max="R$ 245k"
              />
            </div>
          </FeatureMockup>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.25em] text-primary">
              Inteligência de Mercado
            </p>
            <h2 className="mt-3 font-display text-3xl text-foreground sm:text-4xl">
              Saiba se o preço é bom <em className="not-italic text-iridescent">antes</em> de mandar mensagem.
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Pra cada modelo que você acompanha, mostramos quantos estão à venda no Brasil,
              o preço mínimo, médio e máximo. Você sabe na hora se aquele carro é uma oportunidade.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-foreground/80">
              <Bullet>Estatísticas atualizadas todo dia</Bullet>
              <Bullet>Posição do anúncio vs. mercado</Bullet>
              <Bullet>Disponível na conta gratuita</Bullet>
            </ul>
          </div>
        </div>
      </section>

      {/* Desktop CTA */}
      <div className="hidden md:flex flex-col items-center gap-4 pb-16">
        <Link
          to="/login"
          className="rounded-full bg-primary px-10 py-3.5 text-base font-medium text-primary-foreground transition hover:opacity-90"
        >
          Criar conta grátis
        </Link>
        <Link
          to="/planos"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Quer alertas automáticos? R$29/mês →
        </Link>
      </div>

      {/* ─── CTA final + form ──────────────────────────────────────────── */}
      <section id="criar-alerta" className="mx-auto max-w-2xl px-6 pb-8 sm:pb-12">
        {session && hasAlert ? (
          <div className="surface-card rounded-3xl px-6 py-12 text-center sm:px-10">
            <h2 className="font-display text-3xl text-foreground sm:text-4xl">
              Seu radar está ativo.
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Te avisamos assim que surgir uma oportunidade.
            </p>
            <Link
              to="/alertas"
              className="btn-primary mt-8 inline-block rounded-full px-6 py-3 text-sm font-medium"
            >
              Ver meus alertas →
            </Link>
          </div>
        ) : (
          <div className="surface-card rounded-3xl px-6 py-12 sm:px-10">
            <div className="text-center">
              <h2 className="font-display text-3xl text-foreground sm:text-4xl">
                Não achou agora? A gente avisa.
              </h2>
              <p className="mt-3 text-sm text-muted-foreground">
                Configure um alerta em menos de 60 segundos — sem cartão de crédito pra começar.
              </p>
            </div>
            <div className="mx-auto mt-10 max-w-md">
              <Suspense fallback={null}>
                <AlertForm source="home" trackOpen={false} />
              </Suspense>
            </div>
          </div>
        )}
      </section>

      {/* ─── FAQ inline ────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-3xl px-6 pt-8 pb-12 sm:pt-10 sm:pb-20">
        <div className="text-center">
          <h2 className="font-display text-3xl text-foreground sm:text-4xl">
            Dúvidas frequentes.
          </h2>
        </div>

        <div className="mt-10 divide-y dark:divide-white/10 divide-black/10 border-y dark:border-white/10 border-black/10">
          {LANDING_FAQ.map((f, i) => (
            <details key={i} className="group py-4">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-medium text-foreground sm:text-base">
                <span>{f.q}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-foreground/75">{f.a}</p>
            </details>
          ))}
        </div>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          Mais perguntas?{" "}
          <Link to="/faq" className="text-foreground underline underline-offset-4 hover:text-primary">
            Ver FAQ completo →
          </Link>
        </p>
      </section>
    </main>
  );
}

/* ─── Reusable sections ─────────────────────────────────────────────── */

const PRICE_BUCKETS: { label: string; min_price?: number; max_price?: number }[] = [
  { label: "Até R$ 150 mil",      min_price: 100_000, max_price: 150_000 },
  { label: "R$ 150–200 mil",      min_price: 150_000, max_price: 200_000 },
  { label: "R$ 200–300 mil",      min_price: 200_000, max_price: 300_000 },
  { label: "R$ 300–500 mil",      min_price: 300_000, max_price: 500_000 },
  { label: "Acima de R$ 500 mil", min_price: 500_000 },
];

function PriceBuckets() {
  return (
    <section className="mx-auto max-w-6xl px-6 pb-6">
      <h2 className="font-display text-lg text-foreground sm:text-xl mb-4">Por faixa de preço</h2>
      <div className="flex flex-wrap gap-2">
        {PRICE_BUCKETS.map((b) => (
          <Link
            key={b.label}
            to="/buscar"
            search={{ min_price: b.min_price, max_price: b.max_price }}
            className="chip rounded-full px-4 py-2 text-sm text-foreground transition-all duration-150 hover:-translate-y-0.5 hover:border-primary/40 dark:hover:bg-white/[0.08] hover:bg-black/[0.08] hover:shadow-md hover:shadow-primary/10"
          >
            {b.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

function MarketPrices() {
  // Largest cohorts first (most defensible numbers), one row per model so the
  // block doesn't show four BMW 320i years in a row.
  const seen = new Set<string>();
  const rows = [...teaser.rows]
    .sort((a, b) => b.n - a.n)
    .filter((r) => (seen.has(r.label) ? false : (seen.add(r.label), true)))
    .slice(0, 4);

  return (
    <section className="mx-auto max-w-6xl px-6 pb-8 pt-4">
      <h2 className="font-display text-lg text-foreground sm:text-xl">Preços de mercado</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Quanto o mercado pede por um blindado usado: piso, mediana e teto por modelo, versão e ano.
      </p>
      <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map((r) => (
          <li key={`${r.modelSlug}-${r.year}`}>
            <a
              href={cohortSearchHref(r)}
              className="chip block rounded-2xl p-4 transition-all duration-150 hover:-translate-y-0.5 hover:border-primary/40 dark:hover:bg-white/[0.08] hover:bg-black/[0.08] hover:shadow-md hover:shadow-primary/10"
            >
              <span className="block text-sm font-medium text-foreground">
                {r.label} {r.year}
              </span>
              <span className="mt-2 block text-xl font-semibold tabular-nums text-foreground">
                {brl(r.pricep50)}
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                mediana · {brl(r.pricep10)} a {brl(r.pricep90)}
              </span>
            </a>
          </li>
        ))}
      </ul>
      <Link
        to="/tabela"
        className="mt-4 inline-block text-sm text-primary underline-offset-4 hover:underline"
      >
        Ver a tabela completa →
      </Link>
    </section>
  );
}

function FeaturedModels() {
  const top = MODELOS.slice(0, 12);
  // Pull counts from catalog-facets — same queryKey as the hero counter so
  // React Query dedupes to a single network call. Previously we fired one
  // /api/listings?count_only=true per model card (12 Turnstile-gated calls
  // that 401'd on anon users); the matview already has these counts.
  const { data: facets } = useQuery({
    queryKey: ["home-facets"],
    queryFn: fetchCatalogFacets,
    staleTime: 10 * 60 * 1000,
  });
  const counts = top.map((m) =>
    facets?.models.find((mm) => mm.slug === m.slug)?.count
  );

  return (
    <section className="mx-auto max-w-6xl px-6 pt-2 pb-10">
      <h2 className="font-display text-lg text-foreground sm:text-xl">
        Comece pelos mais procurados
      </h2>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {top.map((m, i) => {
          const count = counts?.[i];
          const cta =
            count === undefined
              ? "Ver anúncios →"
              : count === 0
                ? "Sem anúncios no momento"
                : count === 1
                  ? "Ver 1 anúncio →"
                  : `Ver ${count} anúncios →`;
          return (
            <Link
              key={m.slug}
              to="/buscar"
              search={{ models: m.slug }}
              onClick={() => posthog.capture("home_model_clicked", { slug: m.slug, label: m.displayName })}
              className="surface-card group overflow-hidden rounded-2xl transition-all duration-150 hover:-translate-y-0.5 hover:border-primary/50 dark:hover:bg-white/[0.07] hover:bg-black/[0.07] hover:shadow-lg hover:shadow-primary/10"
            >
              <div className="relative aspect-[16/9] overflow-hidden dark:bg-white/[0.04] bg-black/[0.04]">
                <ModelCardImage slug={m.slug} alt={m.displayName} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Car className="h-8 w-8 text-muted-foreground/20" />
                </div>
              </div>
              <div className="p-4">
                <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  {m.brand}
                </p>
                <p className="mt-1 font-display text-sm text-foreground group-hover:text-primary">
                  {m.displayName}
                </p>
                <p className="mt-2 text-xs text-muted-foreground group-hover:text-foreground/70">
                  {cta}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function toListing(l: SearchListing): Listing {
  return {
    id: l.id,
    source: l.source ?? "",
    title: l.title,
    display_title: l.display_title,
    model_slug: l.model_slug,
    year: l.year,
    km: l.km,
    price: l.price,
  };
}

function PriceDropCarousel() {
  const { session } = useSession();
  const { data: listings } = useQuery({
    queryKey: ["home-price-drops"],
    queryFn: fetchPriceDropListings,
    staleTime: 10 * 60 * 1000,
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<Listing | null>(null);
  const [loginGateOpen, setLoginGateOpen] = useState(false);

  function scroll(dir: "left" | "right") {
    scrollRef.current?.scrollBy({ left: dir === "right" ? 320 : -320, behavior: "smooth" });
  }

  if (!listings?.length) return null;

  return (
    <>
      <section className="mx-auto max-w-6xl px-6 py-6">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="font-display text-lg text-foreground sm:text-xl">
            Baixaram de preço recentemente
          </h2>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => scroll("left")}
              className="hidden md:inline-flex h-7 w-7 items-center justify-center rounded-full border dark:border-white/[0.08] border-black/[0.08] text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
              aria-label="Anterior"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => scroll("right")}
              className="hidden md:inline-flex h-7 w-7 items-center justify-center rounded-full border dark:border-white/[0.08] border-black/[0.08] text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
              aria-label="Próximo"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
            <Link to="/buscar" search={{ price_dropped: true }} className="text-xs text-muted-foreground hover:text-foreground md:ml-1">
              Ver todos →
            </Link>
          </div>
        </div>
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        >
          {listings.map((l) => (
            <button
              key={l.id}
              onClick={() => {
                posthog.capture("price_drop_clicked", { listing_id: l.id, price_drop_amount: l.price_drop_amount });
                setSelected(toListing(l));
              }}
              className="surface-card text-left w-44 shrink-0 rounded-2xl p-3.5 transition-all duration-150 hover:-translate-y-0.5 hover:border-primary/60 hover:bg-black/[0.06] dark:hover:bg-white/20 hover:shadow-xl hover:shadow-primary/20"
            >
              <span className="inline-flex items-center rounded-full bg-green-500/10 px-2 py-0.5 text-[11px] font-semibold text-green-600 dark:text-green-400">
                ↓ R$ {fmtBR(l.price_drop_amount ?? 0)}
              </span>
              <p className="mt-2 line-clamp-2 text-sm font-medium leading-snug text-foreground">
                {fmtCarTitle(l.display_title ?? l.title)}
              </p>
              <p className="mt-1.5 text-sm font-semibold text-primary">
                {l.price != null ? `R$ ${fmtBR(l.price)}` : "—"}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {[l.year, l.km != null ? `${fmtBR(l.km)} km` : null].filter(Boolean).join(" · ")}
              </p>
            </button>
          ))}
        </div>
      </section>

      {selected && (
        <ListingDetailDrawer
          listing={selected}
          session={session}
          isInComparador={false}
          onToggleComparador={(l) => {
            const qs = l.model_slug ? `?models=${l.model_slug}` : "";
            window.open(`/buscar${qs}`, "_blank", "noopener");
          }}
          onClose={() => setSelected(null)}
          onOpenSimilar={(l) => setSelected(l)}
          onRequestLogin={() => setLoginGateOpen(true)}
        />
      )}
      {loginGateOpen && <LoginGateModal onClose={() => setLoginGateOpen(false)} />}
    </>
  );
}

function ProFeatureCard({
  icon, title, body, detail,
}: { icon: React.ReactNode; title: string; body: string; detail: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="surface-card cursor-pointer rounded-2xl p-6 transition-all duration-150 hover:border-primary/40"
      onClick={() => setOpen((o) => { if (!o) posthog.capture("feature_card_expanded", { title }); return !o; })}
    >
      <div className="flex items-center gap-3">
        <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </div>
        <h3 className="font-display text-lg text-foreground">{title}</h3>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{body}</p>
      <div className={`grid transition-all duration-300 ease-in-out ${open ? "mt-4 grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">{detail}</div>
      </div>
      <div className="mt-4 flex justify-center">
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </div>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <Check className="mt-[3px] h-3.5 w-3.5 shrink-0 text-primary" />
      <span>{children}</span>
    </li>
  );
}

function CompareRow({ label, a, b, c }: { label: string; a: React.ReactNode; b: React.ReactNode; c: React.ReactNode }) {
  return (
    <tr className="border-t dark:border-white/[0.05] border-black/[0.05]">
      <td className="py-2 pr-3 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</td>
      <td className="px-2 py-2">{a}</td>
      <td className="px-2 py-2">{b}</td>
      <td className="px-2 py-2">{c}</td>
    </tr>
  );
}

function MockStat({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-medium tabular-nums text-foreground/90">{value}</p>
    </div>
  );
}

function PriceSparkline() {
  return (
    <svg viewBox="0 0 280 64" className="mt-3 h-16 w-full" aria-hidden>
      <defs>
        <linearGradient id="ps-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.2" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M0,14 L60,18 L120,26 L180,38 L240,44 L280,54 L280,64 L0,64 Z" fill="url(#ps-fill)" className="text-primary" />
      <path d="M0,14 L60,18 L120,26 L180,38 L240,44 L280,54" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary" />
      {([[60,18],[120,26],[180,38],[240,44],[280,54]] as [number,number][]).map(([x,y],i) => (
        <circle key={i} cx={x} cy={y} r="3" className="fill-primary" />
      ))}
    </svg>
  );
}

function FeatureMockup({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`w-full surface-card rounded-3xl p-6 sm:p-7 ${className}`}>{children}</div>
  );
}

function MarketCard({
  model, active, kmAvg, min, avg, max,
}: { model: string; active: string; kmAvg: string; min: string; avg: string; max: string }) {
  return (
    <div className="rounded-2xl border dark:border-white/[0.07] border-black/[0.07] dark:bg-white/[0.03] bg-black/[0.03] p-4">
      <p className="mb-3 text-xs font-medium text-foreground/70">{model}</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[11px]">
        <MockStat label="Ativos"    value={active} />
        <MockStat label="KM médio"  value={kmAvg} />
        <MockStat label="Mín."      value={min} />
        <MockStat label="Médio"     value={avg} />
        <MockStat label="Máx."      value={max} wide />
      </div>
    </div>
  );
}

function EmailPreview() {
  return (
    <div className="rounded-2xl border dark:border-white/[0.07] border-black/[0.07] dark:bg-white/[0.03] bg-black/[0.03]">
      <div className="flex items-center gap-2 border-b dark:border-white/[0.06] border-black/[0.06] px-4 py-3 text-[11px] text-muted-foreground">
        <Bell className="h-3.5 w-3.5 text-primary" />
        <span className="font-medium text-foreground/90">Busca Blindados</span>
        <span className="ml-auto">há 2 horas</span>
      </div>
      <div className="px-4 pt-4">
        <p className="font-display text-sm text-foreground sm:text-base">
          3 novos blindados no seu radar
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Discovery Sport · Compass · X3 — São Paulo
        </p>
      </div>
      <div className="mt-4 space-y-2 px-4 pb-4">
        {[
          { title: "Land Rover Discovery Sport HSE 2022", km: "32mil", price: "R$ 285.000" },
          { title: "Jeep Compass Limited 2023",           km: "18mil", price: "R$ 220.000" },
          { title: "BMW X3 xDrive 2021",                  km: "42mil", price: "R$ 265.000" },
        ].map((l, i) => (
          <div key={i} className="flex items-center justify-between gap-3 rounded-lg border dark:border-white/[0.05] border-black/[0.05] dark:bg-white/[0.02] bg-black/[0.02] px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11px] font-medium text-foreground/90">{l.title}</p>
              <p className="text-[10px] text-muted-foreground">{l.km} km</p>
            </div>
            <p className="shrink-0 text-[11px] font-medium tabular-nums text-foreground/90">{l.price}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertMockup() {
  return (
    <div className="rounded-xl border dark:border-white/[0.08] border-black/[0.08] dark:bg-white/[0.03] bg-black/[0.03] p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Bell className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-foreground">Novo anúncio: Toyota Corolla Cross</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">2023 · 28.000 km · R$ 189.900</p>
          <p className="mt-1 text-[11px] text-primary">Apareceu agora em OLX →</p>
        </div>
      </div>
      <div className="mt-3 rounded-lg border dark:border-white/[0.06] border-black/[0.06] p-3">
        <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">Seu filtro</p>
        <p className="mt-1 text-[11px] text-foreground/80">Corolla Cross · até 50.000 km · até R$ 200.000</p>
      </div>
    </div>
  );
}

function ComparadorMockup() {
  return (
    <div className="rounded-xl border dark:border-white/[0.08] border-black/[0.08] dark:bg-white/[0.03] bg-black/[0.03] p-4">
      <table className="w-full text-xs text-foreground">
        <thead>
          <tr>
            <th className="pb-2 text-left" />
            <th className="pb-2 text-left font-medium text-foreground/50">Carro A</th>
            <th className="pb-2 text-left font-medium text-foreground/50">Carro B</th>
            <th className="pb-2 text-left font-medium text-foreground/50">Carro C</th>
          </tr>
        </thead>
        <tbody>
          <CompareRow label="Preço" a="R$ 189.900" b="R$ 204.000" c="R$ 198.500" />
          <CompareRow label="Ano" a="2023" b="2022" c="2023" />
          <CompareRow label="KM" a="28.000" b="41.000" c="19.000" />
          <CompareRow
            label="vs. média"
            a={<span className="font-medium text-green-500">−8%</span>}
            b={<span className="font-medium text-red-500">+3%</span>}
            c={<span className="font-medium text-muted-foreground">0%</span>}
          />
        </tbody>
      </table>
    </div>
  );
}

function HistoricoMockup() {
  return (
    <div className="rounded-xl border dark:border-white/[0.08] border-black/[0.08] dark:bg-white/[0.03] bg-black/[0.03] p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-foreground">BMW X1 · 2022</p>
        <span className="text-[11px] font-medium text-green-500">↓ R$ 8.000 desde jan</span>
      </div>
      <PriceSparkline />
      <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
        <span>Jan</span><span>Mar</span><span>Mai</span><span>Jul</span><span>Set</span><span>Nov</span>
      </div>
    </div>
  );
}

function MercadoMockup() {
  return (
    <div className="rounded-xl border dark:border-white/[0.08] border-black/[0.08] dark:bg-white/[0.03] bg-black/[0.03] p-4">
      <p className="text-xs font-medium text-foreground">Discovery Sport Blindado · mercado atual</p>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
        <MockStat label="Anúncios ativos" value="142" />
        <MockStat label="Preço médio" value="R$ 312.000" />
        <MockStat label="Mínimo visto" value="R$ 249.000" />
        <MockStat label="Máximo visto" value="R$ 389.900" />
      </div>
      <div className="mt-3 rounded-lg dark:bg-primary/10 bg-primary/8 p-2.5">
        <p className="text-[10px] text-muted-foreground">Este anúncio</p>
        <div className="mt-0.5 flex items-baseline justify-between">
          <p className="text-sm font-semibold text-foreground">R$ 289.000</p>
          <span className="text-[11px] font-medium text-green-500">−8% vs. média</span>
        </div>
      </div>
    </div>
  );
}

function FeatureRow({
  reverse, icon, title, body, bullets, mockup,
}: {
  reverse: boolean;
  icon: React.ReactNode;
  title: string;
  body: string;
  bullets: string[];
  mockup: React.ReactNode;
}) {
  return (
    <div className={`flex items-center gap-16 py-16 ${reverse ? "flex-row-reverse" : ""}`}>
      <div className="flex-1 space-y-5">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            {icon}
          </div>
          <h3 className="font-display text-2xl text-foreground">{title}</h3>
        </div>
        <p className="text-base leading-relaxed text-muted-foreground">{body}</p>
        <ul className="space-y-2 text-sm text-foreground/75">
          {bullets.map((b) => <Bullet key={b}>{b}</Bullet>)}
        </ul>
      </div>
      <div className="flex-1">{mockup}</div>
    </div>
  );
}
